import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { useClinicProfile } from '../../context/ClinicProfileContext';
import { formatInTimeZone, getTimeZoneLabel, isValidTimeZone } from '../../utils/timeZone';

export interface AppointmentConflictCandidate {
  appointmentId: string;
  appointmentStatus?: string | null;
  appointmentStartDate?: string | null;
  appointmentTitle?: string | null;
  appointmentLocation?: string | null;
}

interface AppointmentConflictResolutionModalProps {
  isOpen: boolean;
  candidates: AppointmentConflictCandidate[];
  recommendedAppointmentId?: string | null;
  onCancel: () => void;
  onConfirm: (appointmentId: string) => void;
}

const formatDateTime = (value: string | null | undefined, timeZone: string | null): string => {
  if (!value) return 'Data não informada';
  if (!timeZone) return 'Horário indisponível';
  try {
    return formatInTimeZone(value, 'DD/MM/YYYY HH:mm', timeZone);
  } catch {
    return 'Data inválida';
  }
};

const AppointmentConflictResolutionModal: React.FC<AppointmentConflictResolutionModalProps> = ({
  isOpen,
  candidates,
  recommendedAppointmentId,
  onCancel,
  onConfirm,
}) => {
  const { profile, isLoading: isClinicProfileLoading } = useClinicProfile();
  const availableTimeZone =
    !isClinicProfileLoading && profile && isValidTimeZone(profile.timeZone)
      ? profile.timeZone
      : null;
  const [displayTimeZone, setDisplayTimeZone] = useState<string | null>(null);
  const initialSelection = useMemo(
    () => recommendedAppointmentId || candidates[0]?.appointmentId || '',
    [recommendedAppointmentId, candidates],
  );
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(initialSelection);

  React.useEffect(() => {
    setSelectedAppointmentId(initialSelection);
  }, [initialSelection]);

  React.useEffect(() => {
    if (!isOpen) {
      setDisplayTimeZone(null);
      return;
    }
    if (!displayTimeZone && availableTimeZone) {
      setDisplayTimeZone(availableTimeZone);
    }
  }, [availableTimeZone, displayTimeZone, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[70]">
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3">
              <div className="bg-amber-100 rounded-full p-2">
                <AlertTriangle className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Conflito de vínculo detectado</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Encontramos dois agendamentos possíveis para esta evolução. Escolha qual deve ser utilizado.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
              title="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-3 mb-6">
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {displayTimeZone
                ? `Horários da clínica: ${getTimeZoneLabel(displayTimeZone)}.`
                : 'Carregando o fuso horário da clínica...'}
            </p>
            {candidates.map((candidate) => {
              const isRecommended = candidate.appointmentId === recommendedAppointmentId;
              const isSelected = candidate.appointmentId === selectedAppointmentId;

              return (
                <label
                  key={candidate.appointmentId}
                  className={`block border rounded-lg p-4 cursor-pointer transition-colors ${
                    isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="appointment-conflict-choice"
                      value={candidate.appointmentId}
                      checked={isSelected}
                      onChange={() => setSelectedAppointmentId(candidate.appointmentId)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="font-semibold text-gray-900">
                          Agendamento {candidate.appointmentId.slice(0, 8)}...
                        </p>
                        {isRecommended && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-semibold">
                            <CheckCircle2 className="w-3 h-3" />
                            Recomendado (appointmentEvolutionId)
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-700 space-y-1">
                        <p><strong>Data:</strong> {formatDateTime(candidate.appointmentStartDate, displayTimeZone)}</p>
                        <p><strong>Status:</strong> {candidate.appointmentStatus || 'Não informado'}</p>
                        <p><strong>Título:</strong> {candidate.appointmentTitle || 'Não informado'}</p>
                        <p><strong>Local:</strong> {candidate.appointmentLocation || 'Não informado'}</p>
                      </div>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => onConfirm(selectedAppointmentId)}
              disabled={!selectedAppointmentId || !displayTimeZone}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Confirmar escolha
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentConflictResolutionModal;
